<template>
  <div class="contact">
    <div class="container">
      <h2 class="section-title">{{ $t('contact.title') }}</h2>

      <div class="contact-content">
        <div class="contact-info">
          <h2>{{ $t('contact.connectTitle') }}</h2>
          <p>{{ $t('contact.connectDescription') }}</p>

          <div class="social-links">
            <div class="social-icons">
              <a href="https://www.linkedin.com/in/leo-martin-pala-7a3a1129a/" target="_blank" rel="noopener noreferrer"
                 class="social-icon" :title="$t('contact.linkedin')">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
              <a href="https://github.com/Leo-Martin-Pala" target="_blank" rel="noopener noreferrer" class="social-icon"
                 :title="$t('contact.github')">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path
                      d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
                </svg>
              </a>
            </div>
          </div>

          <div class="contact-illustration">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="contact-svg">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M22 7l-10 7L2 7"/>
            </svg>
          </div>
        </div>

        <div class="contact-form-container">
          <h2>{{ $t('contact.formTitle') }}</h2>
          <form class="contact-form" @submit.prevent="submitForm">
            <div class="form-group-row">
              <div class="form-group">
                <label for="name">{{ $t('contact.name') }}</label>
                <input
                    type="text"
                    id="name"
                    v-model="form.name"
                    :class="{ 'error': errors.name }"
                    :placeholder="$t('contact.namePlaceholder')"
                    required
                />
                <span v-if="errors.name" class="error-message">{{ $t('contact.required') }}</span>
              </div>

              <div class="form-group">
                <label for="email">{{ $t('contact.email') }}</label>
                <input
                    type="email"
                    id="email"
                    v-model="form.email"
                    :class="{ 'error': errors.email }"
                    placeholder="email@gmail.com"
                    required
                />
                <span v-if="errors.email" class="error-message">
                  {{ errors.email === 'Email is required' ? $t('contact.required') : $t('contact.invalidEmail') }}
                </span>
              </div>
            </div>

            <div class="form-group">
              <label for="subject">{{ $t('contact.subject') }}</label>
              <input
                  type="text"
                  id="subject"
                  v-model="form.subject"
                  :placeholder="$t('contact.subjectPlaceholder')"
              />
            </div>

            <div class="form-group">
              <label for="message">{{ $t('contact.message') }}</label>
              <textarea
                  id="message"
                  v-model="form.message"
                  :class="{ 'error': errors.message }"
                  rows="5"
                  :placeholder="$t('contact.messagePlaceholder')"
                  required
              ></textarea>
              <span v-if="errors.message" class="error-message">{{ $t('contact.required') }}</span>
            </div>

            <button type="submit" class="btn btn-primary submit-btn" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="btn-content">
                <svg class="spinner" viewBox="0 0 24 24">
                  <circle class="path" cx="12" cy="12" r="10" fill="none" stroke-width="4"></circle>
                </svg>
                {{ $t('contact.sending') }}
              </span>
              <span v-else class="btn-content">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                     class="send-icon">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                {{ $t('contact.send') }}
              </span>
            </button>

            <div v-if="formStatus.success" class="success-message">
              {{ $t('contact.success') }}
            </div>

            <div v-if="formStatus.error" class="error-alert">
              {{ $t('contact.error') }}
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import emailjs from '@emailjs/browser'

export default {
  name: 'ContactView',
  data() {
    return {
      form: {
        name: '',
        email: '',
        subject: '',
        message: ''
      },
      errors: {
        name: '',
        email: '',
        message: ''
      },
      isSubmitting: false,
      formStatus: {
        success: false,
        error: false,
        message: ''
      }
    }
  },
  computed: {
    emailPlaceholder() {
      return this.$t('contact.emailPlaceholder')
    }
  },
  methods: {
    validateForm() {
      let isValid = true
      this.errors = {
        name: '',
        email: '',
        message: ''
      }

      if (!this.form.name.trim()) {
        this.errors.name = 'Name is required'
        isValid = false
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!this.form.email.trim()) {
        this.errors.email = 'Email is required'
        isValid = false
      } else if (!emailRegex.test(this.form.email)) {
        this.errors.email = 'Invalid email'
        isValid = false
      }

      if (!this.form.message.trim()) {
        this.errors.message = 'Message is required'
        isValid = false
      }

      return isValid
    },

    submitForm() {
      if (!this.validateForm()) {
        return
      }

      this.isSubmitting = true
      this.formStatus = {
        success: false,
        error: false,
        message: ''
      }

      // Create a formatted timestamp
      const now = new Date()
      const formattedDate = now.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      const formattedTime = now.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
      const timestamp = `${formattedDate} ${formattedTime}`

      const templateParams = {
        name: this.form.name,
        email: this.form.email,
        title: this.form.subject || 'Portfolio Contact Form',
        message: this.form.message,
        time: timestamp
      }
      emailjs
          .send(
              import.meta.env.VITE_EMAILJS_SERVICE_ID,
              import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
              templateParams,
              {
                publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY,
              }
          )
          .then(() => {
            this.isSubmitting = false
            this.formStatus = {
              success: true,
              error: false,
              message: ''
            }

            // Reset form
            this.form = {
              name: '',
              email: '',
              subject: '',
              message: ''
            }

            // Hide success message after 5 seconds
            setTimeout(() => {
              this.formStatus.success = false
            }, 5000)
          })
          .catch(error => {
            console.error('Email sending failed:', error)
            this.isSubmitting = false
            this.formStatus = {
              success: false,
              error: true,
              message: error.text || 'Failed to send email'
            }
          })
    }
  }
}
</script>

<style lang="scss" scoped>
.contact {
  padding: 2rem 0;
}

.section-title {
  margin-bottom: 2rem;
  text-align: center;
  font-size: 2.5rem;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background-color: var(--color-primary);
    margin: 1rem auto 0;
  }
}

.contact-content {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.contact-info {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h2 {
    margin-bottom: 1rem;
    color: var(--color-primary);
    position: relative;
    font-size: 2rem;

    &::after {
      content: '';
      display: block;
      width: 40px;
      height: 3px;
      background-color: var(--color-primary);
      margin-top: 0.5rem;
    }
  }

  p {
    margin-bottom: 2rem;
    line-height: 1.7;
    font-size: 1.1rem;
  }
}

.social-links {
  margin-bottom: 2rem;

  .social-icons {
    display: flex;
    gap: 1rem;
  }

  .social-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background-color: var(--color-card-bg);
    border-radius: 50%;
    color: var(--color-text);
    transition: all 0.3s ease;
    box-shadow: 0 4px 8px var(--color-shadow);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);

    &:hover {
      color: var(--color-primary);
      transform: translateY(-5px) scale(1.1);
      box-shadow: 0 6px 12px var(--color-shadow);
    }
  }
}

.contact-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;

  .contact-svg {
    max-width: 250px;
    color: var(--color-primary);
    opacity: 0.8;
  }
}

.contact-form-container {
  background-color: var(--color-card-bg);
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 10px 30px var(--color-shadow);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;

  h2 {
    margin-bottom: 1.5rem;
    color: var(--color-primary);
    position: relative;
    font-size: 1.8rem;

    &::after {
      content: '';
      display: block;
      width: 40px;
      height: 3px;
      background-color: var(--color-primary);
      margin-top: 0.5rem;
    }
  }
}

.contact-form {
  position: relative;
  z-index: 1;

  .form-group-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 576px) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: 0.95rem;
    color: var(--color-text);
    opacity: 0.9;
  }

  input, textarea {
    width: 100%;
    padding: 1rem 1.2rem;
    border: 1px solid var(--color-border);
    border-radius: 8px;
    background-color: var(--color-background);
    color: var(--color-text);
    transition: all 0.3s ease;
    font-size: 1rem;

    &::placeholder {
      color: var(--color-text);
      opacity: 0.5;
    }

    &:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(var(--color-primary-rgb, 248, 198, 48), 0.2);
      transform: translateY(-2px);
    }

    &.error {
      border-color: #e74c3c;
      box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.2);
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  .error-message {
    color: #e74c3c;
    font-size: 0.875rem;
    margin-top: 0.4rem;
    display: block;
  }

  .submit-btn {
    width: 100%;
    padding: 1rem;
    font-weight: 600;
    font-size: 1.1rem;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;
    overflow: hidden;
    position: relative;
    transition: all 0.4s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 15px rgba(var(--color-primary-rgb, 248, 198, 48), 0.3);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }

    .btn-content {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }

    .send-icon {
      margin-right: 0.25rem;
      transition: transform 0.3s ease;
    }

    &:hover .send-icon {
      transform: translateX(3px);
    }

    .spinner {
      animation: rotate 2s linear infinite;
      width: 18px;
      height: 18px;

      .path {
        stroke: currentColor;
        stroke-linecap: round;
        animation: dash 1.5s ease-in-out infinite;
      }
    }
  }

  @keyframes rotate {
    100% {
      transform: rotate(360deg);
    }
  }

  @keyframes dash {
    0% {
      stroke-dasharray: 1, 150;
      stroke-dashoffset: 0;
    }
    50% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -35;
    }
    100% {
      stroke-dasharray: 90, 150;
      stroke-dashoffset: -124;
    }
  }

  .success-message {
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: rgba(46, 204, 113, 0.1);
    border: 1px solid #2ecc71;
    border-radius: 8px;
    color: #2ecc71;
    text-align: center;
    font-weight: 500;
    animation: fadeIn 0.5s ease-out;
  }

  .error-alert {
    margin-top: 1.5rem;
    padding: 1rem;
    background-color: rgba(231, 76, 60, 0.1);
    border: 1px solid #e74c3c;
    border-radius: 8px;
    color: #e74c3c;
    text-align: center;
    font-weight: 500;
    animation: fadeIn 0.5s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>